import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function MatrixCalculator() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Math", url: "https://calclab.app/math" },
      { name: "Matrix Calculator", url: "https://calclab.app/calculator/matrix" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Matrix Calculator",
      "Calculate determinant, trace, and more for 2x2 and 3x3 matrices. Free linear algebra calculator.",
      "https://calclab.app/calculator/matrix",
      lang
    )
  ]);
  const [size, setSize] = useState("2");
  const [operation, setOperation] = useState("determinant");
  const [matrix, setMatrix] = useState<string[][]>([
    ["", ""],
    ["", ""],
  ]);
  const [result, setResult] = useState<string | null>(null);

  const updateSize = (newSize: string) => {
    setSize(newSize);
    const n = parseInt(newSize);
    const newMatrix = Array(n).fill(null).map(() => Array(n).fill(""));
    setMatrix(newMatrix);
  };

  const updateCell = (i: number, j: number, value: string) => {
    const newMatrix = [...matrix];
    newMatrix[i][j] = value;
    setMatrix(newMatrix);
  };

  const calculate2x2Determinant = (m: number[][]) => {
    return m[0][0] * m[1][1] - m[0][1] * m[1][0];
  };

  const calculate3x3Determinant = (m: number[][]) => {
    return (
      m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) -
      m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) +
      m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0])
    );
  };

  const calculate = () => {
    const numMatrix = matrix.map(row => row.map(cell => parseFloat(cell)));

    if (numMatrix.some(row => row.some(cell => isNaN(cell)))) {
      toast({
        title: "Invalid Input",
        description: "Please fill all cells with valid numbers.",
        variant: "destructive",
      });
      return;
    }

    if (operation === "determinant") {
      let det: number;
      if (size === "2") {
        det = calculate2x2Determinant(numMatrix);
      } else {
        det = calculate3x3Determinant(numMatrix);
      }
      setResult(`Determinant = ${det.toFixed(4)}`);
      toast({
        title: "Calculated",
        description: `Determinant = ${det.toFixed(4)}`,
      });
    } else if (operation === "trace") {
      const trace = numMatrix.reduce((sum, row, i) => sum + row[i], 0);
      setResult(`Trace = ${trace.toFixed(4)}`);
      toast({
        title: "Calculated",
        description: `Trace = ${trace.toFixed(4)}`,
      });
    }
  };

  const reset = () => {
    const n = parseInt(size);
    setMatrix(Array(n).fill(null).map(() => Array(n).fill("")));
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Matrix Calculator" 
      calculatorPath="/calculator/matrix"
      category="math"
      subcategory="Linear Algebra"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Matrix Calculator</CardTitle>
            <CardDescription>
              Calculate determinant, trace, and more
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="size">Matrix Size</Label>
                <Select value={size} onValueChange={updateSize}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2×2</SelectItem>
                    <SelectItem value="3">3×3</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="operation">Operation</Label>
                <Select value={operation} onValueChange={setOperation}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="determinant">Determinant</SelectItem>
                    <SelectItem value="trace">Trace</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Matrix</Label>
              <div className="mt-2 grid gap-2" style={{gridTemplateColumns: `repeat(${size}, 1fr)`}}>
                {matrix.map((row, i) =>
                  row.map((cell, j) => (
                    <Input
                      key={`${i}-${j}`}
                      type="number"
                      step="any"
                      value={cell}
                      onChange={(e) => updateCell(i, j, e.target.value)}
                      className="text-center"
                      placeholder="0"
                    />
                  ))
                )}
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <>
                <Card className="bg-muted/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Result</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{result}</p>
                  </CardContent>
                </Card>

                <CalculatorSuperCalcCTA config={calculatorCTAs["matrix"]} trigger={result} />
              </>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Definitions</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground space-y-2">
                <p><strong>Determinant:</strong> A scalar value that can be computed from a square matrix</p>
                <p><strong>Trace:</strong> Sum of elements on the main diagonal</p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
